import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "@stores/shared/useBannerChannelStore"; // Importing it is what listens for BannerChanged
import useTenantContextStore from "@stores/shared/useTenantContextStore";
import { notifySessionChange, onSessionChange } from "@utils/shared/session-storage-sync";
import {
  BANNER_ALERT_SEVERITY,
  SYSTEM_BANNER_DISMISSED_KEY,
  bannerMessage,
  bannerSignature,
  isBannerActive,
  msUntilBannerChange,
} from "@utils/shared/system-banner";

const readDismissed = () => {
  try {
    return globalThis.sessionStorage?.getItem(SYSTEM_BANNER_DISMISSED_KEY) ?? null;
  } catch {
    return null;
  }
};

// A dismissal holds in every tab for the session (until sign-out or the browser closes), so a
// notice posted again with the same text is seen the next time, and the next user always sees it.
const useDismissal = () => {
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(
    () =>
      onSessionChange((data) => {
        if (data[SYSTEM_BANNER_DISMISSED_KEY]) setDismissed(data[SYSTEM_BANNER_DISMISSED_KEY]);
      }),
    [],
  );

  const dismiss = useCallback((signature) => {
    try {
      globalThis.sessionStorage?.setItem(SYSTEM_BANNER_DISMISSED_KEY, signature);
      notifySessionChange([SYSTEM_BANNER_DISMISSED_KEY]);
    } catch {
      // Storage unavailable: the banner still goes away in this tab.
    }
    setDismissed(signature);
  }, []);

  return [dismissed, dismiss];
};

// Renders again when the banner's display window opens or closes.
const useWindowTick = (banner) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const delay = msUntilBannerChange(banner, Date.now());
    if (delay === null) return undefined;
    const timer = setTimeout(() => setTick((count) => count + 1), delay);
    return () => clearTimeout(timer);
  }, [banner, tick]);
};

/**
 * The system banner to show right now (ADR-0051), or null: { severity, message, onDismiss }.
 * The banner comes from the tenant context, which is read again whenever the live channel says
 * it changed (useBannerChannelStore). The sign-in screen can't use the authenticated hub, so
 * there it stays as read at startup, and `signIn` shows it only when the admin chose to.
 */
export const useSystemBanner = ({ signIn = false } = {}) => {
  const { i18n } = useTranslation();
  const banner = useTenantContextStore((state) => state.banner);
  const [dismissed, dismiss] = useDismissal();

  useWindowTick(banner);

  if (!isBannerActive(banner, Date.now())) return null;
  if (signIn && !banner.showOnSignIn) return null;
  const signature = bannerSignature(banner);
  if (banner.dismissible && dismissed === signature) return null;

  return {
    severity: BANNER_ALERT_SEVERITY[banner.severity] ?? BANNER_ALERT_SEVERITY.info,
    message: bannerMessage(banner, i18n.language),
    onDismiss: banner.dismissible ? () => dismiss(signature) : undefined,
  };
};
